import React, { useContext, useEffect, useRef, useState } from "react";
import { createThemeValues } from "../../../util/themeUtils";
import { WHITE } from "../../../constants/colors";
import { ThemeContext } from "styled-components";
import Text from "../../atoms/text";
import { Box } from "../../atoms/layouts";
import ButtonWithAction from "../../atoms/button-with-action";
import { noop, arrowBorder } from "../../../util/general";
import { fallbackValues } from "./Tooltip.theme";

const TOOLTIP_THEME_SOURCE = "Popover";

const Tooltip = ({
  tooltipID,
  children,
  hasCustomTrigger = false,
  triggerText = "",
  containerExtraStyles = "",
  triggerButtonVariant = "smallGhost",
  content = "",
  contentExtraStyles = "",
  minWidth = "250px",
  maxWidth = "100%",
  height = "auto",
  contentPosition = {
    top: "-110px",
    right: "auto",
    bottom: "auto",
    left: "-225px"
  },
  arrowDirection = "down",
  arrowPosition = {
    arrowTop: "auto",
    arrowRight: "10px",
    arrowBottom: "-8px",
    arrowLeft: "auto"
  },
  customTriggerRole,
  backgroundColor = WHITE
}) => {
  /**
   * closeTimeoutRef is used internally to store a timer ID for delaying tooltip close. It will have a `.current` property (the timer ID) when the effect or event handlers set it.
   */
  const closeTimeoutRef = useRef(null);
  /**
   * containerRef is used to store a reference to the container element. It will have a `.current` property (the container element) when the effect or event handlers set it.
   */
  const containerRef = useRef(null);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const themeContext = useContext(ThemeContext);
  const themeValues = createThemeValues(
    themeContext,
    fallbackValues,
    TOOLTIP_THEME_SOURCE
  );

  const { top, right, bottom, left } = contentPosition;
  const { arrowTop, arrowRight, arrowBottom, arrowLeft } = arrowPosition;

  const handleToggleTooltip = desiredState => {
    if (tooltipOpen !== desiredState) {
      setTooltipOpen(desiredState);
    }
  };

  const handleKeyDown = e => {
    if (e.key === "Escape") {
      handleToggleTooltip(false);
    }
  };

  /**
   * @function renderTrigger
   * Renders the tooltip trigger element.
   *
   * When `hasCustomTrigger` is true, the provided child element is cloned and
   * injected with the event handlers needed to control tooltip visibility:
   *  - onFocus/onBlur: open and close for keyboard users
   *  - onKeyDown: allows Escape to dismiss the tooltip
   *  - onTouchStart: open on tap for touch/mobile users (onFocus is unreliable on touch)
   *
   * Mouse interactions (hover) are handled at the container level via
   * onMouseEnter/onMouseLeave, so they do not need to be injected here.
   *
   * Any existing event handlers on the child are preserved and called first,
   * so the child's own behavior is not overridden.
   *
   * When no custom trigger is provided, a default ButtonWithAction is rendered
   * using `triggerText` and `triggerButtonVariant`.
   */
  const renderTrigger = () => {
    if (hasCustomTrigger && !children) {
      console.warn(
        "Tooltip: children prop is required when hasCustomTrigger is true"
      );
    }

    if (hasCustomTrigger && children) {
      const child = React.Children.only(children);
      /**
       * Capture the child's existing handlers before overwriting
       */
      const {
        onFocus: childOnFocus,
        onBlur: childOnBlur,
        onKeyDown: childOnKeyDown,
        onTouchStart: childOnTouchStart
      } = child.props ?? {};

      /**
       * Clone the child element and add the necessary event handlers
       */
      return React.cloneElement(child, {
        tabIndex: child.props?.tabIndex ?? 0,
        ariaDescribedby: tooltipID,
        style: { cursor: `pointer`, ...child.props?.style },
        onFocus: e => {
          childOnFocus?.(e);
          handleToggleTooltip(true);
        },
        onBlur: e => {
          childOnBlur?.(e);
          handleToggleTooltip(false);
        },
        onKeyDown: e => {
          childOnKeyDown?.(e);
          handleKeyDown(e);
        },
        onTouchStart: e => {
          childOnTouchStart?.(e);
          handleToggleTooltip(true);
        },
        role: customTriggerRole || child.props?.role,
        "aria-describedby": tooltipID,
        "data-qa": `tooltip-trigger-${tooltipID}`
      });
    } else {
      return (
        <ButtonWithAction
          action={noop}
          onKeyDown={handleKeyDown}
          variant={triggerButtonVariant}
          text={triggerText}
          tabIndex={0}
          aria-describedby={tooltipID}
          onFocus={() => handleToggleTooltip(true)}
          onBlur={() => handleToggleTooltip(false)}
          onTouchStart={() => handleToggleTooltip(true)}
          dataQa={`tooltip-trigger-${tooltipID}`}
          extraStyles={`
            color: ${themeValues.linkColor};
            &:hover { color: ${themeValues.hoverColor}; text-decoration: none;}
            &:active, &:focus { color: ${themeValues.activeColor};text-decoration: none;}
            button, span, &:hover span { text-decoration: none; }
          `}
        />
      );
    }
  };

  /**
   * @function handleMouseEnter
   * Handles the mouse enter event for the tooltip container.
   * It clears any existing timeout and opens the tooltip.
   */
  const handleMouseEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    handleToggleTooltip(true);
  };

  /**
   * @function handleMouseLeave
   * Handles the mouse leave event for the tooltip container.
   * It sets a timeout to close the tooltip after 200ms.
   */
  const handleMouseLeave = () => {
    closeTimeoutRef.current = setTimeout(() => {
      handleToggleTooltip(false);
    }, 200);
  };

  /**
   * Handles the touch start event for the tooltip container.
   * It closes the tooltip if the touch is outside the container.
   */
  useEffect(() => {
    if (!tooltipOpen) return;

    const handleOutsideTouch = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setTooltipOpen(false);
      }
    };
    document.addEventListener("touchstart", handleOutsideTouch);
    return () => document.removeEventListener("touchstart", handleOutsideTouch);
  }, [tooltipOpen]);

  /**
   * Cleans up the timeout when the component unmounts.
   */
  useEffect(() => {
    return () => {
      if (closeTimeoutRef?.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  return (
    <Box
      ref={containerRef}
      padding="0"
      extraStyles={`position: relative; ${containerExtraStyles}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      data-qa={`${tooltipID}-container`}
    >
      {renderTrigger()}
      <Box
        role="tooltip"
        id={tooltipID}
        aria-hidden={!tooltipOpen}
        background={backgroundColor}
        data-qa="tooltip-contents"
        extraStyles={`
          position: absolute;
          display: ${tooltipOpen ? "block" : "none"};
          top: ${top};
          right: ${right};
          bottom: ${bottom};
          left: ${left};
          height: ${height};
          color: ${themeValues.textColor};
          ${contentExtraStyles}
        `}
        boxShadow="0px 2px 14px 0px rgb(246, 246, 249), 0px 3px 8px 0px rgb(202, 206, 216)"
        border="1px solid transparent"
        borderRadius="4px"
        minWidth={minWidth}
        maxWidth={maxWidth}
      >
        {typeof content === "string" && content !== "" ? (
          <Text color={themeValues.textColor}>{content}</Text>
        ) : content !== undefined && content !== null ? (
          content
        ) : null}
        <Box
          padding="0"
          extraStyles={`
            position: absolute;
            content: "";
            width: 0;
            height: 0;
            ${arrowBorder(backgroundColor, arrowDirection, "8px")};
            filter: drop-shadow(2px 8px 14px black);
            bottom: ${arrowBottom};
            right: ${arrowRight};
            top: ${arrowTop};
            left: ${arrowLeft};
          `}
        />
      </Box>
    </Box>
  );
};

export default Tooltip;
